<template>
  <section
    id="home-transformations"
    class="editorial-section stories-section"
    :dir="direction"
    aria-labelledby="home-stories-title"
  >
    <div class="editorial-inner">
      <header class="editorial-header">
        <div>
          <p class="eyebrow">{{ t('topbar.successStories') }}</p>
          <h2 id="home-stories-title">{{ t('conversion.storiesTitle') }}</h2>
          <p class="section-intro">{{ t('conversion.storiesIntro') }}</p>
        </div>
        <NuxtLink
          class="section-link"
          :to="localePath({ name: 'stories' })"
          prefetch-on="interaction"
          >{{ t('conversion.storiesLink') }} <EditorialArrow
        /></NuxtLink>
      </header>
      <div v-if="stories.length" class="editorial-grid story-grid">
        <article
          v-for="story in stories"
          :key="story.picture"
          class="story-card"
        >
          <NuxtLink
            :to="localePath({ name: 'stories' })"
            prefetch-on="interaction"
            class="story-link"
          >
            <img
              :src="storyImageUrl(apiBase, story.picture)"
              :alt="
                t('conversion.storyAlt', {
                  name: story.name || t('topbar.successStories')
                })
              "
              width="620"
              height="930"
              loading="lazy"
              decoding="async"
              class="story-image"
            />
            <div class="story-caption">
              <h3 v-if="story.name">{{ story.name }}</h3>
              <p v-if="story.package?.title">{{ story.package.title }}</p>
              <span class="story-arrow" aria-hidden="true"
                ><EditorialArrow
              /></span>
            </div>
          </NuxtLink>
        </article>
      </div>
    </div>
  </section>
  <section
    id="home-magazine"
    class="editorial-section magazine-section"
    :dir="direction"
    aria-labelledby="home-magazine-title"
  >
    <div class="editorial-inner">
      <header class="editorial-header">
        <div>
          <p class="eyebrow">{{ t('blog.label') }}</p>
          <h2 id="home-magazine-title">{{ t('conversion.magazineTitle') }}</h2>
          <p class="section-intro">{{ t('conversion.magazineIntro') }}</p>
        </div>
        <NuxtLink
          class="section-link"
          :to="localePath('/blog')"
          prefetch-on="interaction"
          >{{ t('conversion.magazineLink') }} <EditorialArrow
        /></NuxtLink>
      </header>
      <div v-if="posts.length" class="editorial-grid magazine-grid">
        <EditorialArticleCard
          v-for="post in posts"
          :key="post.id || post.slug"
          :post="post"
        />
      </div>
    </div>
  </section>
</template>
<script setup>
import { useSsrLocalizedApiFetch } from '~/composables/useLocalizedFetch';
import {
  storyImageUrl,
  editorialPosts,
  editorialStories
} from '~/utils/editorial';
const { t, locale } = useI18n();
const localePath = useLocalePath();
const apiBase = useRuntimeConfig().public.API_URL;
const direction = computed(() =>
  ['ar', 'ur'].includes(locale.value) ? 'rtl' : 'ltr'
);
const [{ data: storyData }, { data: postData }] = await Promise.all([
  useSsrLocalizedApiFetch('home-story-preview', '/api/success-stories', {
    query: { page: 1 },
    default: () => ({ items: [] }),
    transform: (data) => ({ items: editorialStories(data) }),
    timeout: 2000,
    retry: 0
  }),
  useSsrLocalizedApiFetch('home-magazine-preview', '/api/blog/most-popular', {
    default: () => ({ posts: [] }),
    transform: (data) => ({ posts: editorialPosts(data) }),
    timeout: 2000,
    retry: 0
  })
]);
const stories = computed(() =>
  (Array.isArray(storyData.value?.items) ? storyData.value.items : [])
    .filter((story) => story && storyImageUrl(apiBase, story.picture))
    .slice(0, 3)
);
const posts = computed(() => editorialPosts(postData.value));
</script>
<style scoped>
.editorial-section {
  padding: clamp(3rem, 6vw, 5rem) 1.5rem;
  color: #17191d;
}
.stories-section {
  background: #f6f5f2;
}
.magazine-section {
  background: #fff;
}
.editorial-inner {
  max-width: 76rem;
  margin-inline: auto;
}
.editorial-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem 3rem;
  margin-block-end: 2rem;
}
.editorial-header > div {
  max-width: 46rem;
}
.eyebrow {
  color: #9a3412;
  font-size: 0.8rem;
  font-weight: 800;
  margin-block-end: 0.7rem;
}
h2 {
  font-size: clamp(1.75rem, 3vw, 2.6rem);
  font-weight: 800;
  line-height: 1.2;
  text-wrap: balance;
}
.section-intro {
  margin-block-start: 1rem;
  color: #444b55;
  line-height: 1.65;
}
.section-link {
  display: inline-flex;
  gap: 0.8rem;
  align-items: center;
  min-height: 44px;
  color: #9a3412;
  font-weight: 750;
  text-decoration: underline;
  text-underline-offset: 0.25rem;
}
.editorial-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.5rem;
}
.story-card {
  min-width: 0;
  overflow: hidden;
  border-radius: 1rem;
  background: #fff;
  border: 1px solid #d7d9dc;
}
.story-link {
  display: flex;
  height: 100%;
  flex-direction: column;
  color: inherit;
  text-decoration: none;
  font-weight: 400;
}
.story-image {
  width: 100%;
  aspect-ratio: 2 / 3;
  height: auto;
  object-fit: contain;
  background: #eae9e5;
}
.story-caption {
  position: relative;
  padding-block: 1.25rem;
  padding-inline: 1.25rem 3rem;
}
.story-caption h3 {
  font-size: 1rem;
  font-weight: 750;
}
.story-caption p {
  margin-block-start: 0.5rem;
  color: #444b55;
  font-size: 0.9rem;
  line-height: 1.5;
}
.story-arrow {
  position: absolute;
  inset-inline-end: 1.25rem;
  inset-block-start: 1.25rem;
  color: #9a3412;
}
.editorial-section :is(h2, h3, p, a) {
  overflow-wrap: anywhere;
}
.story-link:hover h3 {
  text-decoration: underline;
  text-underline-offset: 0.2rem;
}
a:focus-visible {
  outline: 3px solid #c2410c;
  outline-offset: -4px;
  border-radius: 0.4rem;
}
@media (min-width: 640px) {
  .editorial-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (min-width: 1000px) {
  .editorial-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 639px) {
  .editorial-section {
    padding-inline: 1rem;
  }
}
</style>
